import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { ArrowLeft, ExternalLink, MapPin, ShieldCheck, Star } from 'lucide-react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import AdminNav from '../../components/admin/AdminNav'
import ProviderStatusBadge from '../../components/admin/ProviderStatusBadge'
import ReasonModal from '../../components/admin/ReasonModal'
import BookingStatusBadge from '../../components/customer/BookingStatusBadge'
import Button from '../../components/ui/Button'
import FormError from '../../components/ui/FormError'
import { useLang } from '../../contexts/LangContext'
import {
  ADMIN_APPROVE_PROVIDER_MUTATION,
  ADMIN_PROVIDER_DETAIL_QUERY,
  ADMIN_REJECT_PROVIDER_MUTATION,
  ADMIN_SUSPEND_PROVIDER_MUTATION,
  ADMIN_UNSUSPEND_PROVIDER_MUTATION,
  type AdminApproveProviderData,
  type AdminApproveProviderVariables,
  type AdminProviderDetailData,
  type AdminProviderDetailVariables,
  type AdminRejectProviderData,
  type AdminRejectProviderVariables,
  type AdminSuspendProviderData,
  type AdminSuspendProviderVariables,
  type AdminUnsuspendProviderData,
  type AdminUnsuspendProviderVariables,
} from '../../graphql/admin'
import { normalizeLocale } from '../../lib/i18n'
import {
  formatAdminCurrency,
  formatAdminDate,
  formatAdminServiceType,
  formatAdminTimeRange,
} from '../../components/admin/formatters'

function documentKind(url: string | null) {
  if (!url) return 'none'

  const normalized = url.toLowerCase()
  if (normalized.endsWith('.pdf')) return 'pdf'
  if (/\.(png|jpe?g|gif|webp|avif)$/i.test(normalized)) return 'image'
  return 'other'
}

export default function AdminProviderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const { locale, t } = useLang()
  const activeLocale = normalizeLocale(locale)
  const [modalKind, setModalKind] = useState<'reject' | 'suspend' | null>(null)
  const [reason, setReason] = useState('')
  const [actionErrors, setActionErrors] = useState<string[]>([])
  const providerId = id ?? ''

  const backTo = (location.state as { backTo?: string } | null)?.backTo ?? '/admin/providers'

  const { data, loading, error, refetch } = useQuery<AdminProviderDetailData, AdminProviderDetailVariables>(ADMIN_PROVIDER_DETAIL_QUERY, {
    variables: { id: providerId },
    skip: !id,
  })

  const [approveProvider, { loading: approving }] = useMutation<AdminApproveProviderData, AdminApproveProviderVariables>(ADMIN_APPROVE_PROVIDER_MUTATION)
  const [rejectProvider, { loading: rejecting }] = useMutation<AdminRejectProviderData, AdminRejectProviderVariables>(ADMIN_REJECT_PROVIDER_MUTATION)
  const [suspendProvider, { loading: suspending }] = useMutation<AdminSuspendProviderData, AdminSuspendProviderVariables>(ADMIN_SUSPEND_PROVIDER_MUTATION)
  const [unsuspendProvider, { loading: unsuspending }] = useMutation<AdminUnsuspendProviderData, AdminUnsuspendProviderVariables>(ADMIN_UNSUSPEND_PROVIDER_MUTATION)

  if (!id) return <Navigate to="/admin/providers" replace />

  const provider = data?.adminProviderDetail ? data.adminProviderDetail as NonNullable<AdminProviderDetailData['adminProviderDetail']> : null
  const verificationDocumentType = documentKind(provider?.verificationDocumentUrl ?? null)

  function closeModal() {
    setModalKind(null)
    setReason('')
    setActionErrors([])
  }

  async function refreshDetail() {
    setActionErrors([])
    await refetch()
  }

  async function handleApprove() {
    setActionErrors([])

    try {
      const { data: mutationData } = await approveProvider({ variables: { id: providerId } })
      const errors = mutationData?.adminApproveProvider.errors ?? [t('Η έγκριση απέτυχε.', 'Approve failed.')]
      if (errors.length > 0) {
        setActionErrors(errors)
        return
      }

      await refreshDetail()
    } catch {
      setActionErrors([t('Η έγκριση απέτυχε.', 'Approve failed.')])
    }
  }

  async function handleUnsuspend() {
    setActionErrors([])

    try {
      const { data: mutationData } = await unsuspendProvider({ variables: { id: providerId } })
      const errors = mutationData?.adminUnsuspendProvider.errors ?? [t('Η άρση αναστολής απέτυχε.', 'Unsuspend failed.')]
      if (errors.length > 0) {
        setActionErrors(errors)
        return
      }

      await refreshDetail()
    } catch {
      setActionErrors([t('Η άρση αναστολής απέτυχε.', 'Unsuspend failed.')])
    }
  }

  async function handleReasonAction() {
    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      setActionErrors([t('Συμπλήρωσε λόγο για αυτή την ενέργεια.', 'Add a reason for this action.')])
      return
    }

    setActionErrors([])

    try {
      if (modalKind === 'reject') {
        const { data: mutationData } = await rejectProvider({ variables: { id: providerId, reason: trimmedReason } })
        const errors = mutationData?.adminRejectProvider.errors ?? [t('Η απόρριψη απέτυχε.', 'Reject failed.')]
        if (errors.length > 0) {
          setActionErrors(errors)
          return
        }
      } else if (modalKind === 'suspend') {
        const { data: mutationData } = await suspendProvider({ variables: { id: providerId, reason: trimmedReason } })
        const errors = mutationData?.adminSuspendProvider.errors ?? [t('Η αναστολή απέτυχε.', 'Suspend failed.')]
        if (errors.length > 0) {
          setActionErrors(errors)
          return
        }
      }

      closeModal()
      await refreshDetail()
    } catch {
      setActionErrors([
        modalKind === 'reject'
          ? t('Η απόρριψη απέτυχε.', 'Reject failed.')
          : t('Η αναστολή απέτυχε.', 'Suspend failed.'),
      ])
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Link to={backTo} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
        <ArrowLeft className="h-4 w-4" />
        {t('Επιστροφή στους παρόχους', 'Back to providers')}
      </Link>

      <AdminNav />

      {loading && !provider ? (
        <p className="mt-8 text-sm text-slate-500">{t('Φορτώνουμε το προφίλ παρόχου...', 'Loading provider profile...')}</p>
      ) : null}

      {error ? (
        <div className="mt-6 rounded-[1.5rem] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          <p>{t('Δεν ήταν δυνατή η φόρτωση του παρόχου.', 'The provider could not be loaded.')}</p>
          <div className="mt-4">
            <Button type="button" variant="secondary" onClick={() => void refetch()}>
              {t('Δοκίμασε ξανά', 'Try again')}
            </Button>
          </div>
        </div>
      ) : null}

      {!loading && !provider && !error ? (
        <div className="mt-8 rounded-[1.5rem] border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500">
          {t('Ο πάροχος δεν βρέθηκε.', 'Provider not found.')}
        </div>
      ) : null}

      {provider ? (
        <>
          <section className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                {provider.user.avatarUrl ? (
                  <img src={provider.user.avatarUrl} alt="" className="h-16 w-16 rounded-2xl object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 text-xl font-black text-primary-700">
                    {provider.user.firstName[0]}{provider.user.lastName[0]}
                  </div>
                )}
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-3xl font-black tracking-tight text-slate-950">{provider.user.fullName}</h1>
                    <ProviderStatusBadge status={provider.verificationStatus} />
                    {provider.onVacation ? (
                      <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
                        {t('Σε διακοπές', 'On vacation')}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{provider.user.email}</p>
                  <p className="mt-1 text-sm text-slate-500">
                    {t('Μέλος από', 'Member since')} {formatAdminDate(provider.user.createdAt, activeLocale)}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {(provider.verificationStatus === 'pending' || provider.verificationStatus === 'rejected') ? (
                  <Button type="button" loading={approving} onClick={() => void handleApprove()}>
                    {t('Έγκριση', 'Approve')}
                  </Button>
                ) : null}
                {provider.verificationStatus === 'pending' ? (
                  <Button type="button" variant="secondary" onClick={() => { setModalKind('reject'); setActionErrors([]) }}>
                    {t('Απόρριψη', 'Reject')}
                  </Button>
                ) : null}
                {provider.verificationStatus === 'approved' ? (
                  <Button type="button" variant="danger" onClick={() => { setModalKind('suspend'); setActionErrors([]) }}>
                    {t('Αναστολή', 'Suspend')}
                  </Button>
                ) : null}
                {provider.verificationStatus === 'suspended' ? (
                  <Button type="button" loading={unsuspending} onClick={() => void handleUnsuspend()}>
                    {t('Άρση αναστολής', 'Unsuspend')}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="mt-6">
              <FormError errors={actionErrors} />
            </div>

            {provider.rejectionReason ? (
              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <p className="font-semibold text-slate-900">{t('Λόγος απόρριψης', 'Rejection reason')}</p>
                <p className="mt-2">{provider.rejectionReason}</p>
              </div>
            ) : null}

            {provider.suspensionReason ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                <p className="font-semibold text-rose-900">{t('Λόγος αναστολής', 'Suspension reason')}</p>
                <p className="mt-2">{provider.suspensionReason}</p>
              </div>
            ) : null}

            <dl className="mt-8 grid gap-4 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Τηλέφωνο', 'Phone')}</dt>
                <dd className="mt-2">{provider.user.phone || '-'}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Εμπειρία', 'Experience')}</dt>
                <dd className="mt-2">{provider.yearsExperience ? `${provider.yearsExperience}y` : '-'}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Ωριαία τιμή', 'Hourly rate')}</dt>
                <dd className="mt-2">{provider.hourlyRateCents ? formatAdminCurrency(provider.hourlyRateCents, activeLocale) : '-'}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Γλώσσες', 'Languages')}</dt>
                <dd className="mt-2">{provider.languages.length > 0 ? provider.languages.join(', ') : '-'}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Αξιολόγηση', 'Rating')}</dt>
                <dd className="mt-2 inline-flex items-center gap-2 font-semibold text-slate-900">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  {provider.averageRating.toFixed(1)} ({provider.totalReviews})
                </dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Ολοκληρωμένες δουλειές', 'Completed jobs')}</dt>
                <dd className="mt-2 font-semibold text-slate-900">{provider.completedJobs}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Τελευταία ενημέρωση', 'Last updated')}</dt>
                <dd className="mt-2">{formatAdminDate(provider.updatedAt, activeLocale)}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <dt className="font-semibold text-slate-900">{t('Ρόλος λογαριασμού', 'Account role')}</dt>
                <dd className="mt-2 capitalize">{provider.user.role}</dd>
              </div>
            </dl>

            <div className="mt-8 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                {t('Bio', 'Bio')}
              </p>
              <p className="mt-3 text-sm leading-7 text-slate-700">
                {provider.bio || t('Δεν έχει συμπληρωθεί bio ακόμη.', 'No bio has been added yet.')}
              </p>
            </div>
          </section>

          <div className="mt-8 grid gap-6 xl:grid-cols-[1.2fr,0.8fr]">
            <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                    {t('Έγγραφο επαλήθευσης', 'Verification document')}
                  </p>
                  <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                    {t('Review εγγράφων', 'Document review')}
                  </h2>
                </div>
                {provider.verificationDocumentUrl ? (
                  <a
                    href={provider.verificationDocumentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
                  >
                    {t('Άνοιγμα αρχείου', 'Open file')}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : null}
              </div>

              {provider.verificationDocumentUrl ? (
                <>
                  {verificationDocumentType === 'image' ? (
                    <img
                      src={provider.verificationDocumentUrl}
                      alt={t('Έγγραφο επαλήθευσης', 'Verification document')}
                      className="mt-5 max-h-[32rem] w-full rounded-[1.5rem] border border-slate-200 object-contain"
                    />
                  ) : verificationDocumentType === 'pdf' ? (
                    <iframe
                      src={provider.verificationDocumentUrl}
                      title={t('Έγγραφο επαλήθευσης', 'Verification document')}
                      className="mt-5 h-[32rem] w-full rounded-[1.5rem] border border-slate-200 bg-white"
                    />
                  ) : (
                    <div className="mt-5 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600">
                      {t('Το αρχείο δεν μπορεί να γίνει preview εδώ. Άνοιξέ το σε νέο tab.', 'This file cannot be previewed here. Open it in a new tab.')}
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-5 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                  {t('Δεν έχει ανέβει έγγραφο επαλήθευσης.', 'No verification document has been uploaded.')}
                </div>
              )}
            </section>

            <div className="space-y-6">
              <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                  {t('Υπηρεσίες', 'Services')}
                </p>
                <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                  {t('Ενεργές προσφορές', 'Active offerings')}
                </h2>

                <div className="mt-5 space-y-3">
                  {provider.providerServiceOfferings.length > 0 ? provider.providerServiceOfferings.map((offering) => (
                    <div key={offering.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">{formatAdminServiceType(offering.serviceType, activeLocale)}</p>
                      <p className="mt-2 text-sm text-slate-600">
                        {offering.customPriceCents ? formatAdminCurrency(offering.customPriceCents, activeLocale) : t('Χωρίς custom τιμή', 'No custom price')}
                        {offering.customDurationMinutes ? ` · ${offering.customDurationMinutes} min` : ''}
                      </p>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      {t('Δεν έχουν οριστεί υπηρεσίες ακόμη.', 'No services have been configured yet.')}
                    </div>
                  )}
                </div>
              </section>

              <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                  {t('Περιοχές', 'Areas')}
                </p>
                <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                  {t('Service areas', 'Service areas')}
                </h2>

                <div className="mt-5 space-y-3">
                  {provider.serviceAreas.length > 0 ? provider.serviceAreas.map((area) => (
                    <div key={area.id} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <MapPin className="mt-0.5 h-4 w-4 text-primary-600" />
                      <div>
                        <p className="font-semibold text-slate-900">{area.areaName || area.city}</p>
                        <p className="mt-1 text-sm text-slate-600">{area.city} · {area.postalCode}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      {t('Δεν έχουν οριστεί περιοχές ακόμη.', 'No service areas have been configured yet.')}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          <section className="mt-8 rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                  {t('Πρόσφατες κρατήσεις', 'Recent bookings')}
                </p>
                <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                  {t('Τελευταία booking activity', 'Latest booking activity')}
                </h2>
              </div>
              <ShieldCheck className="h-5 w-5 text-slate-400" />
            </div>

            <div className="mt-6 space-y-4">
              {provider.bookings.length > 0 ? provider.bookings.map((booking) => (
                <article key={booking.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <BookingStatusBadge status={booking.status} />
                      <h3 className="mt-3 text-lg font-bold text-slate-950">{formatAdminServiceType(booking.serviceType, activeLocale)}</h3>
                      <p className="mt-2 text-sm text-slate-600">{booking.customer.firstName} {booking.customer.lastName}</p>
                    </div>
                    <p className="text-lg font-bold text-slate-950">{formatAdminCurrency(booking.priceCents, activeLocale)}</p>
                  </div>
                  <dl className="mt-5 grid gap-4 text-sm text-slate-600 md:grid-cols-3">
                    <div>
                      <dt className="font-semibold text-slate-900">{t('Ημερομηνία', 'Date')}</dt>
                      <dd className="mt-1">{formatAdminDate(booking.scheduledDate, activeLocale)}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-slate-900">{t('Ώρα', 'Time')}</dt>
                      <dd className="mt-1">{formatAdminTimeRange(booking.startTime, booking.endTime)}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-slate-900">{t('Πελάτης email', 'Customer email')}</dt>
                      <dd className="mt-1">{booking.customer.email}</dd>
                    </div>
                  </dl>
                </article>
              )) : (
                <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                  {t('Δεν υπάρχουν πρόσφατες κρατήσεις για αυτόν τον πάροχο.', 'There are no recent bookings for this provider.')}
                </div>
              )}
            </div>
          </section>
        </>
      ) : null}

      {modalKind ? (
        <ReasonModal
          title={modalKind === 'reject' ? t('Απόρριψη παρόχου', 'Reject provider') : t('Αναστολή παρόχου', 'Suspend provider')}
          body={
            modalKind === 'reject'
              ? t('Πρόσθεσε σύντομο λόγο που θα αποθηκευτεί μαζί με το verification αποτέλεσμα.', 'Add a short reason that will be stored with the verification outcome.')
              : t('Πρόσθεσε σύντομο λόγο ώστε να καταγραφεί γιατί ο πάροχος μπήκε σε αναστολή.', 'Add a short reason so the suspension is clearly documented.')
          }
          reasonLabel={t('Λόγος', 'Reason')}
          reasonPlaceholder={
            modalKind === 'reject'
              ? t('Π.χ. Το έγγραφο δεν είναι ευανάγνωστο.', 'E.g. The document is not readable.')
              : t('Π.χ. Παραβίαση πολιτικής.', 'E.g. Policy violation.')
          }
          confirmLabel={modalKind === 'reject' ? t('Επιβεβαίωση απόρριψης', 'Confirm reject') : t('Επιβεβαίωση αναστολής', 'Confirm suspend')}
          closeLabel={t('Κλείσιμο', 'Close')}
          loading={modalKind === 'reject' ? rejecting : suspending}
          reason={reason}
          errors={actionErrors}
          onReasonChange={setReason}
          onClose={closeModal}
          onConfirm={() => void handleReasonAction()}
        />
      ) : null}
    </div>
  )
}
